import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Button from '../../components/atoms/Button';
import TextField from '../../components/molecules/TextField';
import {
    COMPOSER_SUGGESTIONS,
    MONITOR_TITLE_MAX_LENGTH,
    PROMPT_MAX_LENGTH,
    TRANSLATION_KEY,
    trackWatchlistEvent,
    WATCHLIST_EVENTS,
} from '../constants';
import { formatScheduleLabel, getComposerScheduleSummary } from '../utils';
import SchedulePopover from './SchedulePopover';
import SlackIcon from '../../resources/svg/icons/slack-icon.svg';
import css from './MonitorComposer.css';

const COMPOSER_KEY = `${TRANSLATION_KEY}.COMPOSER`;

const CollapsedComposer = ({ onOpen }) => (
    <div className={css.cta} role="button" tabIndex={0} onClick={onOpen} onKeyDown={e => e.key === 'Enter' && onOpen()}>
        <span className={css.ctaIcon}>
            <Icon name="plus" size={IconSizes.SMALL} interactive={false} variant={IconVariants.ACTIVE} />
        </span>
        <span className={css.ctaText}>
            <span className={css.ctaTitle}>
                <Translate id={`${COMPOSER_KEY}.CTA_TITLE`} />
            </span>
            <span className={css.ctaBody}>
                <Translate id={`${COMPOSER_KEY}.CTA_BODY`} />
            </span>
        </span>
        <span className={css.ctaHint}>
            <Translate id={`${COMPOSER_KEY}.CTA_HINT`} />
        </span>
    </div>
);

CollapsedComposer.propTypes = {
    onOpen: PropTypes.func.isRequired,
};

const MonitorComposer = ({
    draft = null,
    slackChannelName = null,
    saving = false,
    error = null,
    canSave = false,
    inDialog = false,
    onOpen,
    onChange,
    onCancel,
    onSave,
    onSuggestTitle,
    translate,
}) => {
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    // The composer stays mounted while the page swaps drafts, so a slow suggestion has to be
    // matched against whatever the composer holds once it answers.
    const latestDraft = useRef(draft);
    useEffect(() => {
        latestDraft.current = draft;
    });

    if (!draft) return <CollapsedComposer onOpen={onOpen} />;

    const isEdit = draft.monitorId != null;
    const schedule = getComposerScheduleSummary(draft.cron);
    const scheduleLabel = schedule
        ? translate(`${COMPOSER_KEY}.SCHEDULE_PILL.${schedule.key}`, schedule.data)
        : formatScheduleLabel(draft.cron);

    const suggest = async () => {
        const prompt = draft.prompt.trim();
        if (suggesting || !prompt) return;
        const forMonitorId = draft.monitorId ?? null;
        setSuggesting(true);
        const suggested = await onSuggestTitle(prompt);
        setSuggesting(false);
        const current = latestDraft.current;
        if (!suggested || !current) return;
        if ((current.monitorId ?? null) !== forMonitorId || current.prompt.trim() !== prompt) return;
        onChange({ title: suggested });
    };

    // A new monitor has no name field, so a chip must not leave behind an invisible title that
    // the next chip or a hand-written prompt would contradict - the server names it on save.
    const applySuggestion = key => {
        trackWatchlistEvent(WATCHLIST_EVENTS.COMPOSER_SUGGESTION, { suggestion: key });
        onChange({
            prompt: translate(`${COMPOSER_KEY}.SUGGESTIONS.${key}.PROMPT`),
            title: isEdit ? draft.title || translate(`${COMPOSER_KEY}.SUGGESTIONS.${key}.TITLE`) : '',
        });
    };

    const save = () => {
        if (canSave && !saving && !suggesting) onSave();
    };

    return (
        <div className={classNames(css.composer, { [css.inDialog]: inDialog })}>
            {isEdit && (
            <div className={css.titleRow}>
                <span className={css.editingBadge}>
                    <Translate id={`${COMPOSER_KEY}.EDITING`} />
                </span>
                <TextField
                    value={draft.title}
                    onChange={title => onChange({ title })}
                    disabled={suggesting}
                    placeholder={`${COMPOSER_KEY}.NAME_PLACEHOLDER`}
                    maxLength={MONITOR_TITLE_MAX_LENGTH}
                    containerClass={css.nameField}
                    inputClassName={css.nameInput}
                />
                <Button
                    type="flat"
                    Icon={suggesting ? null : 'sparkles'}
                    iconSize={IconSizes.XX_SMALL}
                    iconVariant={IconVariants.ACTIVE}
                    className={css.suggestLink}
                    disabled={!draft.prompt.trim() || suggesting}
                    onClick={suggest}
                >
                    {suggesting && <span className={css.suggestSpinner} />}
                    <Translate id={`${COMPOSER_KEY}.SUGGEST`} />
                </Button>
            </div>
            )}

            <div className={css.promptRow}>
                <Icon name="sparkles" size={IconSizes.LARGE} interactive={false} className={css.star} />
                <TextField
                    type="textarea"
                    value={draft.prompt}
                    onChange={prompt => onChange({ prompt })}
                    maxLength={PROMPT_MAX_LENGTH}
                    placeholder={`${COMPOSER_KEY}.PLACEHOLDER`}
                    containerClass={css.promptField}
                    inputClassName={css.promptInput}
                    inputConfig={{ rows: 2, autoFocus: true }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            save();
                        }
                    }}
                />
                <Icon
                    name="x"
                    size={IconSizes.X_SMALL}
                    variant={IconVariants.LIGHT}
                    className={css.close}
                    disabled={saving}
                    onClick={saving ? undefined : onCancel}
                />
            </div>

            <div className={css.suggestions}>
                {COMPOSER_SUGGESTIONS.map(key => (
                    <button key={key} type="button" className={css.suggestion} onClick={() => applySuggestion(key)}>
                        <Translate id={`${COMPOSER_KEY}.SUGGESTIONS.${key}.CHIP`} />
                    </button>
                ))}
            </div>

            <div className={css.actionRow}>
                <div className={css.scheduleAnchor}>
                        <Button
                            type="flat"
                            className={css.schedulePill}
                            textClassName={css.scheduleText}
                            Icon="clock"
                            iconSize={IconSizes.XX_SMALL}
                            iconVariant={IconVariants.LIGHT}
                            ariaExpanded={scheduleOpen}
                            onClick={() => setScheduleOpen(open => !open)}
                        >
                            {scheduleLabel}
                            <Icon
                                name="chevron-down"
                                size={IconSizes.XX_SMALL}
                                interactive={false}
                                variant={IconVariants.LIGHT}
                                className={css.scheduleChevron}
                            />
                        </Button>
                    {scheduleOpen && (
                        <SchedulePopover
                            cron={draft.cron}
                            onSave={cron => {
                                onChange({ cron });
                                setScheduleOpen(false);
                            }}
                            onClose={() => setScheduleOpen(false)}
                        />
                    )}
                </div>

                <span className={css.separator} />
                <span className={css.destinationLabel}>
                    <Translate id={`${COMPOSER_KEY}.TELL_ME_IN`} />
                </span>
                <button
                    type="button"
                    aria-pressed={draft.slack}
                    className={classNames(css.destination, { [css.destinationOn]: draft.slack })}
                    onClick={() => onChange({ slack: !draft.slack })}
                >
                    <SlackIcon className={css.slackLogo} />
                    <Translate id={`${COMPOSER_KEY}.SLACK`} />
                </button>

                <div className={css.trailing}>
                    <Button type="flat" className={css.cancel} disabled={saving} onClick={onCancel}>
                        <Translate id={`${COMPOSER_KEY}.CANCEL`} />
                    </Button>
                    <Button
                        type="primary"
                        className={css.save}
                        Icon={isEdit ? null : 'plus'}
                        iconSize={IconSizes.XX_SMALL}
                        disabled={!canSave || suggesting}
                        showSpinner={saving}
                        onClick={save}
                    >
                        <Translate id={`${COMPOSER_KEY}.${isEdit ? 'SAVE_CHANGES' : 'ADD'}`} />
                    </Button>
                </div>
            </div>

            {draft.slack && (
                <div className={css.slackNote}>
                    <Translate
                        id={`${COMPOSER_KEY}.${slackChannelName ? 'SLACK_CONNECTED' : 'SLACK_WILL_CONNECT'}`}
                        data={slackChannelName ? { channel: slackChannelName } : undefined}
                    />
                </div>
            )}
            {error && <div className={css.error}>{error}</div>}
        </div>
    );
};

MonitorComposer.propTypes = {
    draft: PropTypes.shape({
        monitorId: PropTypes.string,
        title: PropTypes.string.isRequired,
        prompt: PropTypes.string.isRequired,
        cron: PropTypes.string.isRequired,
        slack: PropTypes.bool.isRequired,
    }),
    slackChannelName: PropTypes.string,
    saving: PropTypes.bool,
    error: PropTypes.string,
    canSave: PropTypes.bool,
    inDialog: PropTypes.bool,
    onOpen: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onSuggestTitle: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(MonitorComposer);
